import { BrandMark } from '../components/BrandMark';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { formatCents, formatTokens } from '../hooks/useExecution';
import { useActivationFunnel, useGrowthScorecard } from '../hooks/useOnboarding';
import { Input } from '../components/ui/input';
import {
  useSubmissions,
  useApproveTemplate,
  useRejectTemplate,
  useDeadLetters,
  useDeadLetterStats,
  useReplayDeadLetter,
  useDiscardDeadLetter,
  useAdminUsers,
  useUpdateUser,
  useDeleteUser,
  useAdminCosts,
} from '../hooks/useAdmin';

type Tab = 'moderation' | 'dlq' | 'users' | 'funnel' | 'costs';

export default function Admin() {
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>('moderation');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link
              to="/dashboard"
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <span className="text-sm font-medium text-foreground">Admin</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6">
          <h2 className="text-3xl font-bold mb-2">Admin</h2>
          <p className="text-muted-foreground">
            Moderate the marketplace, manage failed background tasks, and see what
            the platform costs to run.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <TabButton active={tab === 'moderation'} onClick={() => setTab('moderation')}>
            Template Moderation
          </TabButton>
          <TabButton active={tab === 'dlq'} onClick={() => setTab('dlq')}>
            Dead-Letter Queue
          </TabButton>
          <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
            Users
          </TabButton>
          <TabButton active={tab === 'funnel'} onClick={() => setTab('funnel')}>
            Activation
          </TabButton>
          <TabButton active={tab === 'costs'} onClick={() => setTab('costs')}>
            Costs
          </TabButton>
        </div>

        {tab === 'moderation' && <ModerationPanel />}
        {tab === 'dlq' && <DeadLetterPanel />}
        {tab === 'users' && <UsersPanel />}
        {tab === 'funnel' && <FunnelPanel />}
        {tab === 'costs' && <CostsPanel />}
      </main>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

// ── Template moderation ────────────────────────────────────────

function ModerationPanel() {
  const { data, isLoading } = useSubmissions('PENDING');
  const approve = useApproveTemplate();
  const reject = useRejectTemplate();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  if (isLoading) return <LoadingSkeleton className="h-40" />;

  const submissions = data?.data ?? [];

  if (submissions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          🎉 No templates pending review.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {submissions.length} template{submissions.length === 1 ? '' : 's'} awaiting review
      </p>
      {submissions.map((t) => (
        <Card key={t.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-lg">{t.name}</CardTitle>
                <CardDescription>
                  {t.category}
                  {t.author?.name ? ` · by ${t.author.name}` : ''}
                </CardDescription>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={() => approve.mutate(t.id)}
                  disabled={approve.isPending}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRejectingId(rejectingId === t.id ? null : t.id);
                    setReason('');
                  }}
                >
                  Reject
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {t.description && (
              <p className="text-sm text-muted-foreground mb-3">{t.description}</p>
            )}
            {rejectingId === t.id && (
              <div className="space-y-2 border-t pt-3">
                <Textarea
                  placeholder="Reason for rejection (shown to the author)…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={!reason.trim() || reject.isPending}
                    onClick={() =>
                      reject.mutate(
                        { templateId: t.id, reason: reason.trim() },
                        { onSuccess: () => setRejectingId(null) }
                      )
                    }
                  >
                    Confirm reject
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRejectingId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Dead-letter queue ──────────────────────────────────────────

function DeadLetterPanel() {
  const { data, isLoading } = useDeadLetters();
  const { data: stats } = useDeadLetterStats();
  const replay = useReplayDeadLetter();
  const discard = useDiscardDeadLetter();

  if (isLoading) return <LoadingSkeleton className="h-40" />;

  const entries = data?.data ?? [];

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Pending" value={stats.pending} />
          <Stat label="Replayed" value={stats.replayed} />
          <Stat label="Discarded" value={stats.discarded} />
          <Stat label="Total" value={stats.total} />
        </div>
      )}

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            ✅ No failed tasks in the dead-letter queue.
          </CardContent>
        </Card>
      ) : (
        entries.map((e) => (
          <Card key={e.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <CardTitle className="text-base font-mono">{e.taskType}</CardTitle>
                  <CardDescription>
                    {e.errorType} · {e.attemptCount} attempt
                    {e.attemptCount === 1 ? '' : 's'} ·{' '}
                    {new Date(e.createdAt).toLocaleString()}
                  </CardDescription>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => replay.mutate(e.id)}
                    disabled={replay.isPending}
                  >
                    Replay
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => discard.mutate(e.id)}
                    disabled={discard.isPending}
                  >
                    Discard
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-600 break-words">{e.errorMessage}</p>
              {e.executionId && (
                <p className="text-xs text-muted-foreground mt-1">
                  Execution: {e.executionId}
                </p>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

// ── User management ────────────────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-red-100 text-red-800',
  INACTIVE: 'bg-slate-100 text-slate-700',
  PENDING_VERIFICATION: 'bg-amber-100 text-amber-800',
};

function UsersPanel() {
  const me = useAuthStore((s) => s.user);
  const isSuperAdmin = me?.role === 'SUPER_ADMIN';
  const [search, setSearch] = useState('');
  const { data, isLoading } = useAdminUsers(search);
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const users = data?.data ?? [];

  function setStatus(id: string, status: string) {
    updateUser.mutate({ userId: id, data: { status } });
  }
  function handleDelete(id: string, email: string) {
    if (
      window.confirm(
        `Delete ${email}? This removes the account (e.g. a test/spam user). It won't appear in the list anymore.`
      )
    ) {
      deleteUser.mutate(id);
    }
  }
  function setRole(id: string, role: string) {
    updateUser.mutate({ userId: id, data: { role } });
  }
  function setComp(id: string, isComped: boolean) {
    updateUser.mutate({ userId: id, data: { isComped } });
  }

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      {isLoading ? (
        <LoadingSkeleton className="h-40" />
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No users found.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isSelf = u.id === me?.id;
                    const suspended = u.status === 'SUSPENDED';
                    return (
                      <tr key={u.id} className="border-b last:border-0">
                        <td className="px-4 py-3">
                          <div className="font-medium">
                            {[u.firstName, u.lastName].filter(Boolean).join(' ') ||
                              '—'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {u.email}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs">{u.role}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ${
                              STATUS_BADGE[u.status] ?? 'bg-slate-100'
                            }`}
                          >
                            {u.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {u.isComped ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
                              Comped · Pro
                            </span>
                          ) : u.role !== 'USER' ? (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                              Admin · Pro
                            </span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                              Free
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-end gap-2">
                            {isSelf ? (
                              <span className="text-xs text-muted-foreground">
                                You
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={updateUser.isPending}
                                  onClick={() =>
                                    setStatus(
                                      u.id,
                                      suspended ? 'ACTIVE' : 'SUSPENDED'
                                    )
                                  }
                                >
                                  {suspended ? 'Reactivate' : 'Suspend'}
                                </Button>
                                {u.role === 'USER' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={updateUser.isPending}
                                    onClick={() => setComp(u.id, !u.isComped)}
                                  >
                                    {u.isComped ? 'Revoke Pro' : 'Grant Pro'}
                                  </Button>
                                )}
                                {isSuperAdmin && u.role !== 'SUPER_ADMIN' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={updateUser.isPending}
                                    onClick={() =>
                                      setRole(
                                        u.id,
                                        u.role === 'ADMIN' ? 'USER' : 'ADMIN'
                                      )
                                    }
                                  >
                                    {u.role === 'ADMIN'
                                      ? 'Revoke admin'
                                      : 'Make admin'}
                                  </Button>
                                )}
                                {u.role !== 'SUPER_ADMIN' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700"
                                    disabled={deleteUser.isPending}
                                    onClick={() => handleDelete(u.id, u.email)}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {updateUser.isError && (
        <p className="text-sm text-red-600">
          Couldn't update the user (you may lack permission).
        </p>
      )}
    </div>
  );
}

// ── Activation funnel ──────────────────────────────────────────
function ScorecardStrip() {
  const { data } = useGrowthScorecard();
  if (!data) return null;
  const cards = [
    { label: 'Users', value: data.totalUsers },
    { label: 'Activated', value: `${data.activatedUsers} · ${Math.round(data.activationRate * 100)}%` },
    { label: 'Paying', value: data.payingSubscribers },
    { label: 'Trialing', value: data.trialingSubscribers },
    { label: 'Est. MRR', value: `$${data.estimatedMrrUsd}` },
    { label: 'Repeat rate', value: `${Math.round(data.repeatRate * 100)}%` },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <Card key={c.label}>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{c.label}</p>
            <p className="text-xl font-semibold">{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FunnelPanel() {
  const { data, isLoading } = useActivationFunnel();
  if (isLoading) return <LoadingSkeleton className="h-40" />;
  if (!data) return <ScorecardStrip />;

  const stages = [
    { label: 'Signed up', value: data.signedUp },
    { label: 'Created an agent', value: data.createdAgent },
    { label: 'Ran an agent', value: data.ranAgent },
    { label: 'Activated · first successful run', value: data.activated },
  ];
  const top = data.signedUp || 1;
  const pct = (n: number) => Math.round((n / top) * 100);
  const stepPct = (from: number, to: number) =>
    from ? Math.round((to / from) * 100) : 0;

  return (
    <>
      <ScorecardStrip />
      <Card>
      <CardContent className="space-y-4 p-6">
        <div>
          <h3 className="font-semibold">Activation funnel</h3>
          <p className="text-sm text-muted-foreground">
            Distinct users reaching each stage. Activation = a user&apos;s first
            successful agent run.
          </p>
        </div>
        <div className="space-y-3">
          {stages.map((s, i) => (
            <div key={s.label}>
              <div className="mb-1 flex justify-between text-sm">
                <span>{s.label}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {s.value} · {pct(s.value)}%
                  {i > 0 &&
                    ` · ${stepPct(stages[i - 1].value, s.value)}% of prev`}
                </span>
              </div>
              <div className="h-3 rounded bg-slate-100">
                <div
                  className="h-3 rounded bg-primary"
                  style={{ width: `${pct(s.value)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm">
          <span className="font-medium">Activation rate:</span>{' '}
          {pct(data.activated)}% of signups reach a successful run.
        </p>
        <div className="grid grid-cols-2 gap-4 border-t pt-4">
          <div>
            <p className="text-xs text-muted-foreground">Median time to activate</p>
            <p className="text-lg font-semibold">
              {fmtHours(data.medianTimeToActivateHours)}
            </p>
            <p className="text-xs text-muted-foreground">signup → first success</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Repeat rate</p>
            <p className="text-lg font-semibold">
              {Math.round(data.repeatRate * 100)}%
            </p>
            <p className="text-xs text-muted-foreground">of runners ran more than once</p>
          </div>
        </div>
      </CardContent>
      </Card>
    </>
  );
}

function fmtHours(h: number | null): string {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}

// ──────────────────────────────────────────────────────────────
// Platform costs
// ──────────────────────────────────────────────────────────────

/**
 * What the whole platform spends on LLM tokens — every user, not just us.
 *
 * Distinct from /analytics/costs, which only ever shows a caller their own
 * agents. Free users run on the platform's provider key, so this is the number
 * that turns up on our Anthropic/OpenAI invoice.
 */
function CostsPanel() {
  const [days, setDays] = useState(30);
  const { data, isLoading, error } = useAdminCosts(days);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Could not load platform costs.</p>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const maxDaily = Math.max(...data.daily.map((d) => d.costCents), 0.0001);
  // Two independently-summed floats: a positive epsilon (~1e-17) would make
  // this truthy and print "$0.00 on users' own keys" — asserting spend that
  // does not exist. Threshold at a hundredth of a cent.
  const byoSpend = data.totalCostCents - data.billableCostCents;
  const hasByoSpend = byoSpend > 0.001;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 text-sm rounded-md border transition-colors ${
              days === d
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-muted-foreground hover:text-foreground'
            }`}
          >
            {d} days
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Our bill ({days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCents(data.billableCostCents)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Excludes {data.byoKeyUsers} user{data.byoKeyUsers === 1 ? '' : 's'} on
              their own key
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Projected / month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCents(data.projectedMonthlyCostCents)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              At the last {days} days' rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Runs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatTokens(data.totalRuns)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatTokens(data.totalTokens)} tokens
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gross spend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCents(data.totalCostCents)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {hasByoSpend ? `${formatCents(byoSpend)} on users' own keys` : 'All on our key'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily spend</CardTitle>
          <CardDescription>Estimated, from recorded token usage</CardDescription>
        </CardHeader>
        <CardContent>
          {data.daily.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">
              No runs in this window.
            </p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {data.daily.map((d) => (
                <div
                  key={d.date}
                  className="flex-1 bg-primary/80 hover:bg-primary rounded-t transition-colors min-h-[2px]"
                  style={{ height: `${(d.costCents / maxDaily) * 100}%` }}
                  title={`${d.date}: ${formatCents(d.costCents)} (${d.runs} runs)`}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Top spenders</CardTitle>
          <CardDescription>
            Who is consuming tokens. Users on their own key cost us nothing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.topSpenders.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">
              Nobody has run an agent in this window.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 font-medium">User</th>
                    <th className="py-2 font-medium">Plan</th>
                    <th className="py-2 font-medium text-right">Runs</th>
                    <th className="py-2 font-medium text-right">Tokens</th>
                    <th className="py-2 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topSpenders.map((s) => (
                    <tr key={s.userId} className="border-b last:border-0">
                      <td className="py-2">
                        {s.email}
                        {s.hasOwnKey && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-slate-100 text-muted-foreground">
                            own key
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            s.isPro
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {s.isPro ? 'Pro' : 'Free'}
                        </span>
                      </td>
                      <td className="py-2 text-right tabular-nums">{s.runs}</td>
                      <td className="py-2 text-right tabular-nums">
                        {formatTokens(s.tokens)}
                      </td>
                      <td className="py-2 text-right tabular-nums font-medium">
                        {formatCents(s.costCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Costs are estimates from recorded token counts and the rates in
        <code className="mx-1">services/llm/pricing.py</code>. Verify those against the
        provider's pricing page — a stale rate makes every number here confidently
        wrong. Runs before 17 Jul 2026 recorded $0 and cannot be recovered.
      </p>
    </div>
  );
}
